import DataGrid from "components/data-grid/DataGrid";
import {
  setBreadCrumbs,
  setCurrent,
  setNavigationComponents,
} from "features/navigation/navigationSlice";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, Outlet, useLocation } from "react-router-dom";
import PrimaryButton from "ui/buttons/PrimaryButton";
import { columns } from "./columns";
import { useNavigate } from "react-router-dom";
import DropDownButton from "ui/buttons/DropDownButton";
import { FaEdit } from "react-icons/fa";
import { PencilAltIcon, XIcon } from "@heroicons/react/outline";
import moment, { Moment } from "moment";
import Card from "ui/Card";
import customAxios from "middleware/customAxios";

export default function DiscountsIndex() {
  let navigate = useNavigate();
  let dispatch = useDispatch();
  const userData = useSelector((state) => state.data.userData);
  const permissions = userData?.permissions;
  // Allow if any role has w-discount (permissions is array of role permission arrays)
  const canAddDiscount =
    Array.isArray(permissions) &&
    permissions.some(
      (rolePerms) =>
        Array.isArray(rolePerms) && rolePerms.includes("w-discount")
    );
  const [discounts, setDiscounts] = React.useState([]);
  const [productVariantsMap, setProductVariantsMap] = React.useState({});
  const location = useLocation();

  const fetchDiscounts = React.useCallback(() => {
    customAxios.get("/admin/discounts").then((res) => {
      const list = res?.data?.data ?? res?.data;
      setDiscounts(Array.isArray(list) ? list : []);
    });
  }, []);

  React.useEffect(() => {
    customAxios.get("/admin/product-variants").then((res) => {
      const list = res?.data?.data ?? res?.data ?? [];
      const arr = Array.isArray(list) ? list : [];
      const map = {};
      arr.forEach((v) => {
        if (v && v.id != null) {
          const pName = v.product?.name ?? "";
          const vName = v.name ?? "";
          map[v.id] = [pName, vName].filter(Boolean).join(" - ") || String(v.id);
        }
      });
      setProductVariantsMap(map);
    });
  }, []);

  React.useEffect(() => {
    dispatch(
      setCurrent({
        name: "Discounts",
      })
    );
    dispatch(
      setNavigationComponents(
        canAddDiscount ? (
          <PrimaryButton
            title="Add Discounts"
            onClick={() => navigate("/discounts/add")}
          />
        ) : null
      )
    );
    dispatch(
      setBreadCrumbs([
        {
          name: "Home",
          path: "/home",
        },
        {
          name: "Sales",
          path: "/sales",
        },
      ])
    );
  }, [canAddDiscount, dispatch, navigate]);

  React.useEffect(() => {
    fetchDiscounts();
  }, [fetchDiscounts]);

  React.useEffect(() => {
    if (location.pathname === "/discounts" || location.pathname === "/discounts/") {
      fetchDiscounts();
    }
  }, [location.pathname, fetchDiscounts]);

  const getProductsDisplay = (row) => {
    if (row.isApplicableToAllProducts === true) return "All Products";
    const fromApi = row.products != null && String(row.products).trim() !== "" && row.products !== "—";
    if (fromApi) return row.products;
    const raw = row.selectedProductVariantIds;
    if (!raw) return "—";
    try {
      const ids = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (!Array.isArray(ids) || ids.length === 0) return "—";
      const names = ids
        .map((id) => productVariantsMap[id] ?? (id != null ? `#${id}` : null))
        .filter(Boolean);
      return names.length ? names.join(", ") : "—";
    } catch (_) {
      return "—";
    }
  };

  const cellFunction = (cell) => {
    if (cell.column.id === "products") {
      const row = cell.row?.original ?? {};
      const display = getProductsDisplay(row);
      return <div className="text-left">{display}</div>;
    }
    if (cell.column.id === "expireOn") {
      const row = cell.row?.original ?? {};
      const endDate = row.endDate;
      const display = endDate
        ? moment(endDate).format("DD MMM YYYY")
        : "—";
      return <div className="text-left">{display}</div>;
    }
    // if (cell.column.id === "name") {
    //   return (
    //     <div
    //       onClick={() => {
    //         navigate("/sales/view/" + cell.row.original.id, {
    //           state: cell.row.original,
    //         });
    //       }}
    //       className="flex flex-row items-center uppercase space-x-3 cursor-pointer"
    //     >
    //       <p className="hover:underline">{cell.value}</p>
    //     </div>
    //   );
    // }
    // if (cell.column.id === "actions") {
    //   return (
    //     <DropDownButton
    //       items={[
    //         {
    //           title: "Edit",
    //           icon: <PencilAltIcon className="h-5 w-5" />,
    //           path: "/sales/edit/" + cell.row.original.id,
    //           state: cell.row.original,
    //         },
    //         {
    //           title: "Delete",
    //           icon: <XIcon className="h-5 w-5" />,
    //           path: "/sales/delete/" + cell.row.original.id,
    //           state: cell.row.original,
    //         },
    //       ]}
    //       title="Actions"
    //       icon={<i className="fas fa-ellipsis-v"></i>}
    //       className=""
    //       setSelected={(item) => {
    //         console.log(item);
    //       }}
    //       path="/sales/"
    //     />
    //   );
    // }
    return cell.value;
  };

  return (
    <div className="relative overflow-scroll min-h-[80vh] ">
      <Card
        heading={"All Discounts"}
        description={`You can find all the discounts here`}
      >
        <div className="relative flex space-y-3 flex-col w-full">
          <DataGrid
            searchable={true}
            x_columns={columns}
            testData={discounts}
            height={"h-[calc(100vh-230px)]"}
            showHeader={true}
            cellFunction={cellFunction}
            searchColumn="name"
            searchPrioritizeColumn="name"
          />
        </div>
      </Card>
      <Outlet />
    </div>
  );
}
